import React from 'react';
import { Form } from 'antd';
import type { FormProps } from 'antd';
import { useTranslation } from 'react-i18next';
import { useBrandAssets } from '../../../../theme/useBrandAssets';
import { NS } from '../../i18n';
import './PasswordScreen.css';

/**
 * The pre-session screen where a password is set: the invitation and the reset
 * link (CIB-1166). A copy of the Design prototype's first access (`#terms`,
 * `src/pages/terms/FirstAccessDemo`), which itself reproduces the production
 * login: the brand's background and lockup, «Welcome aboard.» and a glass card.
 *
 * The prototype's three legal checkboxes are left out on purpose: accepting the
 * documents needs an acceptance record in the backend (who, which document,
 * which version, when), which belongs to the Terms and Conditions project, not
 * to the password policy.
 *
 * The card goes in `children`, built with `PasswordCard`.
 */
export function PasswordScreen({ children }: { children: React.ReactNode }) {
  const { t } = useTranslation(NS);
  const { assets, meta } = useBrandAssets();

  return (
    <main className="pw-screen">
      <div className="pw-screen__brand">
        <div className="pw-screen__logo" role="img" aria-label={meta.title} />
        <h1 className="pw-screen__welcome">{t('screen.welcome')}</h1>
      </div>

      {children}

      {/* Same rule as the login footer: every brand shows its website, and its
          tagline only when it has one (SL-1920). */}
      <footer className="pw-screen__foot">
        {meta.tagline && <span>{meta.tagline}</span>}
        <span>{meta.website}</span>
      </footer>
    </main>
  );
}

interface PasswordCardProps {
  title?: React.ReactNode;
  subtitle?: React.ReactNode;
  /** The card's main action. It stays under the body: in view on desktop,
   *  pinned to the bottom edge on narrow screens. */
  action?: React.ReactNode;
  children?: React.ReactNode;
  /** When given, the card is this AntD form, so `action` can submit it. */
  form?: FormProps;
}

/**
 * The glass card of `PasswordScreen`. The body scrolls inside the card on
 * desktop, so the action never leaves the screen (PRD §10 of the prototype):
 * with the requirements open, the card no longer fits a short window.
 */
export function PasswordCard({ title, subtitle, action, children, form }: PasswordCardProps) {
  const content = (
    <>
      {title && <h2 className="pw-screen__title">{title}</h2>}
      {subtitle && <p className="pw-screen__subtitle">{subtitle}</p>}
      <div className="pw-screen__body">{children}</div>
      {action}
    </>
  );

  return form ? (
    <Form {...form} className="pw-screen__card">
      {content}
    </Form>
  ) : (
    <section className="pw-screen__card">{content}</section>
  );
}

PasswordCard.defaultProps = {
  title: undefined,
  subtitle: undefined,
  action: undefined,
  children: undefined,
  form: undefined,
};
